import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAccount, useContract, useSigner } from 'wagmi';

import masterConAbi from 'config/contractsABI/masterAbi.json';

import {
  selectInstrumentsPrice,
  selectSelectedInstrument,
  selectFees,
  selectGasPrice,
  selectContracts,
  selectTokens,
} from 'store/entities/market/selectors';
import { selectCreateOrderLoading } from 'store/entities/trade/orders/selectors';
import { instrumentPairBalanceSelector } from 'store/entities/wallet/selectors';
import { isCountryBlacklistedSelector } from 'store/entities/config/selectors';
import { createOrder } from 'store/entities/trade/orders/thunks';
import { addNotification } from 'store/entities/notification/slice';

import { signCreateOrder, approveToken } from 'utils/smartcontract/orderServices';

import CreateBase from 'components/createBase';
import CreateQuote from 'components/createQuote';
import { Loader } from 'elements/loader';

import { OrderSide, OrderType, NotificationType, Notifications, PriceT, TokenT } from 'types';

import './MainCreateMarketLimitOrder.scss';

interface Props {
  orderType: OrderType;
}

const MainCreateMarketLimitOrder = ({ orderType }: Props) => {
  const dispatch = useDispatch();
  const contracts = useSelector(selectContracts);
  const tokensList = useSelector(selectTokens);
  const isForbiddenRegion: boolean = useSelector(isCountryBlacklistedSelector);
  const currentInstrument = useSelector(selectSelectedInstrument);
  const instrumentsPrice = useSelector(selectInstrumentsPrice);
  const fee = useSelector(selectFees);
  const isCreateOrderLoding = useSelector(selectCreateOrderLoading);
  const balancesFromWallet = useSelector(instrumentPairBalanceSelector);
  const gasPrice = useSelector(selectGasPrice);

  const [isSignOrderLoading, setSignOrderLoading] = useState(false);

  const [baseCurrency, setBaseCurrency] = useState('');
  const [quoteCurrency, setQuoteCurrency] = useState('');

  const [tradeSide, setTradeSide] = useState(OrderSide.Sell);
  const [amount, setAmount] = useState('0');
  const [limitPrice, setLimitPrice] = useState('0');

  const [isErrorBase, setIsErrorBase] = useState(false);
  const [isErrorQuote, setIsErrorQuote] = useState(false);

  const [walletBalance, setWalletBalance] = useState({ base: '0', quote: '0' });

  const { isConnected, address } = useAccount();
  const { data: signer, isError: isSignerError, isLoading } = useSigner();

  const isMarket = orderType === OrderType.Market;

  const masterContract = useContract({
    address: contracts?.master || '',
    abi: masterConAbi,
    signerOrProvider: signer,
  });

  useEffect(() => {
    if (Object.keys(balancesFromWallet)) {
      setWalletBalance({
        base: balancesFromWallet?.baseCurrency,
        quote: balancesFromWallet?.quoteCurrency,
      });
    }
  }, [balancesFromWallet]);

  useEffect(() => {
    if (currentInstrument) {
      setBaseCurrency(currentInstrument.base_currency_id);
      setQuoteCurrency(currentInstrument.quote_currency_id);
      setTradeSide(OrderSide.Sell);

      setAmount('0');
    }
  }, [currentInstrument, orderType]);

  useEffect(() => {
    if (!instrumentsPrice.length) return;
    const filterPrices = instrumentsPrice.find(
      ({ instrument_id }: PriceT) => instrument_id === currentInstrument?.instrument_id
    );

    setLimitPrice(String(tradeSide === OrderSide.Sell ? filterPrices?.price?.ask : filterPrices?.price?.bid));
  }, [instrumentsPrice, currentInstrument, tradeSide]);

  const handlerChangeCurrency = () => {
    const currencyA = baseCurrency;
    const currencyB = quoteCurrency;

    if (tradeSide === OrderSide.Sell) {
      setTradeSide(OrderSide.Buy);
      setWalletBalance({ ...walletBalance, quote: balancesFromWallet?.quoteCurrency });
    } else {
      setTradeSide(OrderSide.Sell);
      setWalletBalance({ ...walletBalance, base: balancesFromWallet?.baseCurrency });
    }

    setBaseCurrency(currencyB);
    setQuoteCurrency(currencyA);
    setAmount('0');
  };

  const handlerCreateOrder = async () => {
    if (!isConnected) return;
    if (isForbiddenRegion) return;

    if (contracts && masterContract && currentInstrument && address && tokensList && fee && signer) {
      setSignOrderLoading(true);

      const baseToken: TokenT = tokensList[currentInstrument.base_currency_id];
      const quoteToken: TokenT = tokensList[currentInstrument.quote_currency_id];

      const order = {
        baseToken,
        quoteToken,
        limitPrice,
        amount,
      };

      let isTokenApproved = true;
      try {
        const token = tradeSide === OrderSide.Sell ? baseToken : quoteToken;
        await approveToken(signer, token, amount, contracts.escrow);
      } catch (error) {
        dispatch(addNotification({ type: NotificationType.Canceled, msg: Notifications.CreatedOrderError }));
        isTokenApproved = false;
      }

      let txHash;
      if (isTokenApproved) {
        try {
          txHash = await signCreateOrder({
            order,
            orderType,
            tradeSide,
            masterContract,
            signer,
            gasPrice,
            slippage: fee.slippage,
          });
        } catch (error) {
          dispatch(addNotification({ type: NotificationType.Canceled, msg: Notifications.CreatedOrderError }));
        }
      }

      if (txHash) {
        dispatch(createOrder(address));
      }

      setSignOrderLoading(false);
    }
  };

  const handlerErrorBase = (err: boolean) => {
    setIsErrorBase(err);
  };

  const handlerErrorQuote = (err: boolean) => {
    setIsErrorQuote(err);
  };

  const getButtonText = () => {
    return baseCurrency === currentInstrument?.base_currency_id
      ? `Sell ${currentInstrument?.base_currency_id}`
      : `Buy ${currentInstrument?.base_currency_id}`;
  };

  const isCreateButtonAvailable = isConnected
    ? isCreateOrderLoding ||
      isSignOrderLoading ||
      isErrorBase ||
      isErrorQuote ||
      !amount ||
      amount === '0' ||
      limitPrice <= '0'
    : false;

  console.log('fee', fee);
  return (
    <div className={`main-create-market-limit-order ${isMarket ? 'market' : 'limit'}`}>
      <CreateBase
        instrument={currentInstrument}
        currency={baseCurrency}
        walletBalance={tradeSide === OrderSide.Sell ? walletBalance.base : walletBalance.quote}
        networkFee={fee?.gas_deposit}
        orderSide={tradeSide}
        handlerChangeAmount={setAmount}
        handlerError={handlerErrorBase}
        orderType={orderType}
        handlerChangeCurrency={handlerChangeCurrency}
        limitPrice={limitPrice}
      />
      {isMarket && <div className="main-create-market-limit-order-switch-bt" onClick={() => handlerChangeCurrency()} />}
      <CreateQuote
        label={isMarket ? _t('Receive', 'CREATE_ORDER.RECEIVE') : _t('Limit Price', 'CREATE_ORDER.LIMIT_PRICE')}
        modeMarket={isMarket}
        instrument={currentInstrument}
        currency={quoteCurrency}
        amount={amount}
        currentPrice={limitPrice}
        baseCurrencyAmount={amount}
        tradingFee={fee?.trading_fee}
        networkFee={fee?.gas_deposit}
        orderSide={tradeSide}
        handlerChangeLimitPrice={setLimitPrice}
        handlerError={handlerErrorQuote}
        orderType={orderType}
      />
      <button
        className={`main-create-market-limit-order-set-connect-btn ${isConnected ? 'connected' : ''}`}
        onClick={handlerCreateOrder}
        disabled={isCreateButtonAvailable}
      >
        {(isCreateOrderLoding || isSignOrderLoading) && <Loader />}
        {isConnected ? getButtonText() : _t('Connect Wallet', 'HEADER.CONNECT_WALLET')}
      </button>
    </div>
  );
};

export default MainCreateMarketLimitOrder;
